import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { parseISO } from "date-fns";
import { Clock, Users, CalendarDays, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { LeaveRequest } from "@/lib/api";

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseDay(s: string): Date {
  const d = parseISO(s);
  d.setHours(0, 0, 0, 0);
  return d;
}

// ── Stat card — matches Organization / CertificationsModule pattern ───────────

function StatCard({
  icon: Icon, label, description, value, bg, color, loading, onClick,
}: {
  icon: React.ElementType; label: string; description: string; value: number;
  bg: string; color: string; loading?: boolean; onClick?: () => void;
}) {
  const inner = (
    <div className={`flex min-h-[116px] items-start gap-3.5 px-5 py-4 w-full ${onClick ? "hover:bg-muted/40 transition-colors" : ""}`}>
      <div className={`h-9 w-9 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-xs font-semibold leading-tight text-foreground">{label}</span>
        <span className="text-[11px] leading-snug text-muted-foreground">{description}</span>
        {loading ? (
          <Skeleton className="mt-1 h-7 w-10" />
        ) : (
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground leading-none">{value}</p>
        )}
      </div>
    </div>
  );

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="h-full w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {inner}
    </button>
  ) : (
    <div>{inner}</div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface LeaveDashboardProps {
  requests: LeaveRequest[];
  isLoading: boolean;
  onClickPending?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function LeaveDashboard({ requests, isLoading, onClickPending }: LeaveDashboardProps) {
  const { t } = useTranslation();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const stats = useMemo(() => {
    const now = today.getTime();

    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    const todayEndTs = todayEnd.getTime();

    const weekStart = new Date(today);
    const day = weekStart.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    weekStart.setDate(weekStart.getDate() + diffToMon);
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);

    let pendingCount = 0;
    let currentlyOnLeave = 0;
    let upcomingThisWeek = 0;
    let monthDaysTaken = 0;
    const monthEmployeeIds = new Set<number>();

    for (const r of requests) {
      const start = parseDay(r.startDate).getTime();
      const end = parseDay(r.endDate);
      end.setHours(23, 59, 59, 999);
      const endTs = end.getTime();

      if (r.status === "Pending") pendingCount++;

      if (r.status === "Approved") {
        if (start <= todayEndTs && endTs >= now) currentlyOnLeave++;
        if (start > todayEndTs && start <= weekEnd.getTime()) upcomingThisWeek++;
        if (start <= monthEnd.getTime() && endTs >= monthStart.getTime()) {
          monthDaysTaken += r.workingDays;
          monthEmployeeIds.add(r.employeeId);
        }
      }
    }

    return { pendingCount, currentlyOnLeave, upcomingThisWeek, monthDaysTaken, monthEmployeesAbsent: monthEmployeeIds.size };
  }, [requests, today]);

  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 sm:divide-x divide-border/60 divide-y sm:divide-y-0">
        <StatCard
          icon={Clock}
          label={t("leave.pendingRequestsStat")}
          description={t("leave.pendingRequestsStatDesc")}
          value={isLoading ? 0 : stats.pendingCount}
          bg="bg-amber-500/10"
          color="text-amber-600"
          loading={isLoading}
          onClick={onClickPending}
        />
        <StatCard
          icon={Users}
          label={t("leave.currentlyOnLeave")}
          description={t("leave.currentlyOnLeaveDesc")}
          value={isLoading ? 0 : stats.currentlyOnLeave}
          bg="bg-blue-500/10"
          color="text-blue-600"
          loading={isLoading}
        />
        <StatCard
          icon={CalendarDays}
          label={t("leave.upcomingThisWeek")}
          description={t("leave.upcomingThisWeekDesc")}
          value={isLoading ? 0 : stats.upcomingThisWeek}
          bg="bg-violet-500/10"
          color="text-violet-600"
          loading={isLoading}
        />
        <StatCard
          icon={TrendingUp}
          label={t("leave.totalDaysTaken")}
          description={t("leave.totalDaysTakenDesc")}
          value={isLoading ? 0 : stats.monthDaysTaken}
          bg="bg-emerald-500/10"
          color="text-emerald-600"
          loading={isLoading}
        />
        <StatCard
          icon={Users}
          label={t("leave.employeesAbsent")}
          description={t("leave.employeesAbsentDesc")}
          value={isLoading ? 0 : stats.monthEmployeesAbsent}
          bg="bg-orange-500/10"
          color="text-orange-600"
          loading={isLoading}
        />
      </div>
    </div>
  );
}
